import { IonCard, IonCardContent, IonCardHeader, IonCardTitle, IonCol } from "@ionic/react";
import { EditReportWeekParams, ReportWeek } from "../../models/reportModels";
import { useRef, useState } from "react";
import ReportWeekEntryDialog from "./ReportWeekEntryDialog";
import { useAppDispatch } from "../../data/redux/hooks";
import ReportService from "../../data/services/ReportService";
import { setError } from "../../data/redux/errorSlice";
import { error500Text } from "../../util/error-helper";
import { QuillDisplayer } from "../../components";
import { setReportWeek } from "../../data/redux/reportWeekSlice";
import TrainerCommentBubble from "../../components/reportComponents/TrainerCommentBubble";

import "./ReportBook.scss";


interface RecordBookWeekProps {
    reportWeek: ReportWeek | undefined;
    editable: boolean
}


const RecordBookWeek: React.FC<RecordBookWeekProps> = ({ reportWeek, editable }: RecordBookWeekProps) => {
    const companyDisplayRef = useRef<HTMLDivElement | null>(null);
    const schoolDisplayRef = useRef<HTMLDivElement | null>(null);
    const otherDisplayRef = useRef<HTMLDivElement | null>(null);
    const dispatch = useAppDispatch();
    const selectedTypeRef = useRef<string>("")
    const [editDialog, setEditDialog] = useState<boolean>(false);


    const callback = async (content) => {

        let editorContent: string | null
        if (typeof content === "string") {
            editorContent = content
        } else {
            editorContent = JSON.stringify(content)
        }
        if (editorContent === "[{\"insert\":\"\\n\"}]") {
            editorContent = null
        }

        let data: EditReportWeekParams = {
            year: reportWeek.year,
            week: reportWeek.week,
            company_activities: reportWeek.company_activities,
            school_activities: reportWeek.school_activities,
            other_activities: reportWeek.other_activities
        }
        data[selectedTypeRef.current] = editorContent

        try {
            const res = await ReportService.editReportWeekEntry(data)
            if (res.report) {
                dispatch(setReportWeek(res.report))
            } else if (res.error) {
                dispatch(setError({
                    isOpen: true,
                    message: res.error
                }))
            } else {
                dispatch(setError({
                    isOpen: true,
                    message: error500Text
                }))
            }

        } catch (err) {
            console.log(err)
            dispatch(setError({
                isOpen: true,
                message: error500Text
            }))
        }
    }

    const openEditDialog = (type: string) => {
        selectedTypeRef.current = type;
        setEditDialog(true)
    }

    const handleCardClick = (type: "company_activities" | "school_activities" | "other_activities") => (e: React.MouseEvent<HTMLDivElement>) => {
        if ((e.target as HTMLElement).closest(".trainerCommentBubble")) return
        openEditDialog(type)
    }

    return (
        <>
            <IonCol size="12">
                <div
                    onClick={editable ? handleCardClick("company_activities") : undefined}
                    style={editable ? { cursor: "pointer" } : undefined}
                >
                    <IonCard className={editable ? "editable reportCard weekCard" : "reportCard weekCard"}>
                        <IonCardHeader >
                        <IonCardTitle>
                            Betriebliche Tätigkeiten
                        </IonCardTitle>
                    </IonCardHeader>
                    <IonCardContent>
                        <div
                            className="weeklyEntryContainer"
                            onClick={editable ? (e) => { e.stopPropagation(); openEditDialog("company_activities"); } : undefined}
                            style={editable ? { cursor: "pointer" } : undefined}
                        >
                            {reportWeek.company_activities ?
                                <QuillDisplayer
                                    ref={companyDisplayRef}
                                    defaultValue={JSON.parse(reportWeek.company_activities)}
                                /> :
                                <i>Noch nichts eingetragen</i>}
                        </div>
                        {reportWeek.company_activities_comments?.length ? (
                            <TrainerCommentBubble comments={reportWeek.company_activities_comments} />
                        ) : null}
                    </IonCardContent>
                </IonCard>
                </div>
            </IonCol>
            <IonCol size="12">
                <div
                    onClick={editable ? handleCardClick("school_activities") : undefined}
                    style={editable ? { cursor: "pointer" } : undefined}
                >
                    <IonCard className={editable ? "editable reportCard weekCard" : "reportCard weekCard"}>
                        <IonCardHeader >
                        <IonCardTitle>
                            Berufsschule (Unterrichtsthemen)
                        </IonCardTitle>
                    </IonCardHeader>
                    <IonCardContent>
                        <div
                            className="weeklyEntryContainer"
                            onClick={editable ? (e) => { e.stopPropagation(); openEditDialog("school_activities"); } : undefined}
                            style={editable ? { cursor: "pointer" } : undefined}
                        >
                            {reportWeek.school_activities ?
                                <QuillDisplayer
                                    ref={schoolDisplayRef}
                                    defaultValue={JSON.parse(reportWeek.school_activities)}
                                /> :
                                <i>Noch nichts eingetragen</i>}
                        </div>
                        {reportWeek.school_activities_comments?.length ? (
                            <TrainerCommentBubble comments={reportWeek.school_activities_comments} />
                        ) : null}
                    </IonCardContent>
                </IonCard>
                </div>
            </IonCol>
            <IonCol size="12">
                <div
                    onClick={editable ? handleCardClick("other_activities") : undefined}
                    style={editable ? { cursor: "pointer" } : undefined}
                >
                    <IonCard className={editable ? "editable reportCard weekCard" : "reportCard weekCard"}>
                        <IonCardHeader >
                        <IonCardTitle>
                            Unterweisungen, Lehrgespräche, betrieblicher Unterricht, außerbetriebliche Schulungsveranstaltungen, Zwischenprüfung
                        </IonCardTitle>
                    </IonCardHeader>
                    <IonCardContent>
                        <div
                            className="weeklyEntryContainer"
                            onClick={editable ? (e) => { e.stopPropagation(); openEditDialog("other_activities"); } : undefined}
                            style={editable ? { cursor: "pointer" } : undefined}
                        >
                            {reportWeek.other_activities ?
                                <QuillDisplayer
                                    ref={otherDisplayRef}
                                    defaultValue={JSON.parse(reportWeek.other_activities)}
                                /> :
                                <i>Noch nichts eingetragen</i>}
                        </div>
                        {reportWeek.other_activities_comments?.length ? (
                            <TrainerCommentBubble comments={reportWeek.other_activities_comments} />
                        ) : null}
                    </IonCardContent>
                </IonCard>
                </div>
            </IonCol>

            <ReportWeekEntryDialog
                isOpen={editDialog}
                setIsOpen={setEditDialog}
                callback={callback}
                value={reportWeek[selectedTypeRef.current]}
                type={selectedTypeRef.current}
            />
        </>
    )
}

export default RecordBookWeek;